/**
 * `topdown-2d` schema (design §15) — a floor plan for top-down action-adventure
 * (StarTropics-like): rooms (world-rect + intent tags), corridors (edges with width and
 * optional key-locks), and key placements. Structural invariants live here; the
 * reachability/feel checks live in validate.ts.
 */
import { z } from 'zod';

export const ROOM_KINDS = ['start', 'goal', 'combat', 'puzzle', 'boss', 'normal'] as const;
export type RoomKind = (typeof ROOM_KINDS)[number];

const RoomSchema = z.object({
  id: z.string().min(1),
  /** World rect (m); x/y is the room's min corner in plan view. */
  x: z.number().finite(),
  y: z.number().finite(),
  w: z.number().finite().positive(),
  h: z.number().finite().positive(),
  kind: z.enum(ROOM_KINDS).default('normal'),
  /** Content palette tag resolved against 03-content (design §10.1). */
  hazard: z.string().optional(),
  difficulty: z.number().finite().min(0).max(1).optional(),
});

const CorridorSchema = z.object({
  fromId: z.string().min(1),
  toId: z.string().min(1),
  /** Passable width (m) — must fit the body + clearance. */
  width: z.number().finite().positive(),
  /** Key id that locks this corridor's door (omit = open). */
  lockedBy: z.string().optional(),
});

const KeySchema = z.object({
  id: z.string().min(1),
  /** Room the key is placed in. */
  roomId: z.string().min(1),
});

/** Entity placement (design §17): an instance of a Content item in a room —
 *  enemies, pickups, NPCs, switches. `ref` is the EXACT Content item name;
 *  extra typed fields ride along (patrol, count, dialogue key, …). */
const TopdownEntitySchema = z
  .object({
    id: z.string().min(1),
    ref: z.string().min(1),
    roomId: z.string().min(1),
    /** Optional offset within the room (m from its min corner). */
    x: z.number().finite().optional(),
    y: z.number().finite().optional(),
    /** Behavior (design §17) — structured, build-agent-consumable (shared with spatial):
     *  hp = hits to defeat; movement = how it moves; patrolRange = patrol span (m). */
    hp: z.number().finite().positive().optional(),
    movement: z.enum(['static', 'patrol', 'chase']).optional(),
    patrolRange: z.number().finite().positive().optional(),
    patrolSec: z.number().finite().positive().optional(),
    /** Cyclic on/off timing (shared with spatial) — previs + game read the same. */
    cycle: z
      .object({
        loopSec: z.number().finite().positive(),
        activeSec: z.number().finite().positive(),
        telegraphSec: z.number().finite().nonnegative().optional(),
        phase: z.number().finite().min(0).max(1).optional(),
      })
      .optional(),
  })
  .passthrough();

export const TopdownLevelSchema = z
  .object({
    archetype: z.literal('topdown-2d'),
    targetSeconds: z.number().finite().positive(),
    rooms: z.array(RoomSchema).min(1),
    corridors: z.array(CorridorSchema).default([]),
    keys: z.array(KeySchema).default([]),
    entities: z.array(TopdownEntitySchema).optional(),
  })
  .superRefine((lvl, ctx) => {
    const roomIds = new Set<string>();
    lvl.rooms.forEach((r, i) => {
      if (roomIds.has(r.id)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: `duplicate room id: ${r.id}`,
          path: ['rooms', i, 'id'],
        });
      }
      roomIds.add(r.id);
    });
    const starts = lvl.rooms.filter((r) => r.kind === 'start');
    if (starts.length !== 1) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: `exactly one 'start' room required (found ${starts.length})`,
        path: ['rooms'],
      });
    }
    if (!lvl.rooms.some((r) => r.kind === 'goal')) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "at least one 'goal' room required",
        path: ['rooms'],
      });
    }
    const entityIds = new Set<string>();
    (lvl.entities ?? []).forEach((e, i) => {
      if (entityIds.has(e.id)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: `duplicate entity id: ${e.id}`,
          path: ['entities', i, 'id'],
        });
      }
      entityIds.add(e.id);
      if (!roomIds.has(e.roomId)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: `entity roomId not found: ${e.roomId}`,
          path: ['entities', i, 'roomId'],
        });
      }
    });
    const keyIds = new Set<string>();
    lvl.keys.forEach((k, i) => {
      if (keyIds.has(k.id)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: `duplicate key id: ${k.id}`,
          path: ['keys', i, 'id'],
        });
      }
      keyIds.add(k.id);
      if (!roomIds.has(k.roomId)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: `key roomId not found: ${k.roomId}`,
          path: ['keys', i, 'roomId'],
        });
      }
    });
    lvl.corridors.forEach((c, i) => {
      if (!roomIds.has(c.fromId)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: `corridor fromId not found: ${c.fromId}`,
          path: ['corridors', i, 'fromId'],
        });
      }
      if (!roomIds.has(c.toId)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: `corridor toId not found: ${c.toId}`,
          path: ['corridors', i, 'toId'],
        });
      }
      if (c.fromId === c.toId) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'corridor connects a room to itself',
          path: ['corridors', i],
        });
      }
      if (c.lockedBy !== undefined && !keyIds.has(c.lockedBy)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: `lockedBy references unknown key: ${c.lockedBy}`,
          path: ['corridors', i, 'lockedBy'],
        });
      }
    });
  });

export type TopdownLevel = z.infer<typeof TopdownLevelSchema>;
export type TopdownRoom = TopdownLevel['rooms'][number];
export type TopdownCorridor = TopdownLevel['corridors'][number];
